import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleIconButton } from '@/components/ui/CircleIconButton';
import { FilterChip } from '@/components/ui/FilterChip';
import { SearchField } from '@/components/ui/SearchField';
import styles from './styles.module.scss';

export interface MatchesFilterBarProps {
  liveOnly: boolean;
  onLiveOnlyChange: (value: boolean) => void;
  /** At least one match of the day is in play — turns the Live dot red and pulsing. */
  hasLive: boolean;
  byTime: boolean;
  onByTimeChange: (value: boolean) => void;
  query: string;
  onQueryChange: (value: string) => void;
  /** Every visible group is collapsed — the toggle then offers "Expand all". */
  allCollapsed: boolean;
  onToggleAll: () => void;
  /** True in by-time mode or when there are no groups to toggle. */
  collapseAllDisabled: boolean;
}

/**
 * Controlled filter row of the `/matches` day view: Live and By time chips,
 * a team/league text filter (tablet and up) and the collapse/expand-all toggle.
 */
export const MatchesFilterBar: React.FC<MatchesFilterBarProps> = ({
  liveOnly,
  onLiveOnlyChange,
  hasLive,
  byTime,
  onByTimeChange,
  query,
  onQueryChange,
  allCollapsed,
  onToggleAll,
  collapseAllDisabled,
}) => {
  const { t } = useTranslation(['league']);

  return (
    <div className={styles.bar}>
      <FilterChip
        label={t('league:live')}
        pressed={liveOnly}
        onClick={() => onLiveOnlyChange(!liveOnly)}
        leading={(
          <span
            className={`${styles.liveDot}${hasLive ? ` ${styles.liveDotActive}` : ''}`}
            aria-hidden="true"
          />
        )}
        shape="rounded"
        className={styles.chip}
        testId="matches-filter-live"
      />
      <FilterChip
        label={t('league:byTime')}
        pressed={byTime}
        onClick={() => onByTimeChange(!byTime)}
        shape="rounded"
        className={styles.chip}
        testId="matches-filter-bytime"
      />

      <div className={styles.search}>
        <SearchField
          variant="pill"
          value={query}
          onChange={onQueryChange}
          placeholder={t('league:filterPlaceholder')}
          testId="matches-filter-query"
        />
      </div>

      <div className={styles.end}>
        <CircleIconButton
          icon={allCollapsed ? 'chevrons-down' : 'chevrons-up'}
          ariaLabel={allCollapsed ? t('league:expandAll') : t('league:collapseAll')}
          onClick={onToggleAll}
          shape="square"
          disabled={collapseAllDisabled}
          ariaExpanded={collapseAllDisabled ? undefined : !allCollapsed}
          testId="matches-toggle-all"
        />
      </div>
    </div>
  );
};

export default MatchesFilterBar;
